import { Redirect, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
} from 'react-native';
import { FadeIn } from '../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../src/components/primitives';
import { useAuth } from '../../src/contexts/AuthContext';
import { loginHrefFor } from '../../src/features/auth/returnTo';
import { MessageInputBar } from '../../src/features/whatsapp/components/MessageInputBar';
import { TemplatePickerSheet } from '../../src/features/whatsapp/components/TemplatePickerSheet';
import { WhatsAppMessageBubble } from '../../src/features/whatsapp/components/WhatsAppMessageBubble';
import { WindowStatusBanner } from '../../src/features/whatsapp/components/WindowStatusBanner';
import {
  useMarkConversationRead,
  useSendWhatsAppMessage,
  useSyncWhatsAppTemplates,
  useWhatsAppMessages,
  useWhatsAppStream,
  useWhatsAppTemplates,
  useWhatsAppWindow,
} from '../../src/features/whatsapp/hooks/useWhatsApp';
import { toChronological } from '../../src/features/whatsapp/lib/messageOrder';
import type { WhatsAppMessage, WhatsAppTemplate } from '../../src/features/whatsapp/types';
import { isWhatsAppEnabled } from '../../src/lib/featureFlags';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';

function WhatsAppChatScreen() {
  const { conversationId } = useLocalSearchParams<{ conversationId: string }>();
  const router = useRouter();
  const { currentOrganization, getAccessToken } = useAuth();
  const orgId = currentOrganization?.id;

  const [showTemplates, setShowTemplates] = useState(false);
  const listRef = useRef<FlatList<WhatsAppMessage>>(null);

  // The newest message is now the last row, so the feed has to follow it — on
  // first paint and on every arrival.
  const scrollToLatest = useCallback(() => {
    listRef.current?.scrollToEnd({ animated: false });
  }, []);

  const {
    data: messagesData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWhatsAppMessages(conversationId, orgId);
  const { data: windowStatus } = useWhatsAppWindow(conversationId, orgId);
  const { data: templates, isLoading: templatesLoading } = useWhatsAppTemplates(orgId);
  const sendMessage = useSendWhatsAppMessage(conversationId ?? '', orgId ?? '');
  const markRead = useMarkConversationRead(conversationId ?? '', orgId ?? '');
  const syncTemplates = useSyncWhatsAppTemplates();

  useWhatsAppStream(orgId, getAccessToken);

  useEffect(() => {
    if (conversationId) {
      markRead.mutate();
    }
  }, [conversationId, markRead.mutate]);

  const messages = useMemo(() => toChronological(messagesData?.pages), [messagesData]);

  const handleSend = useCallback(
    (text: string) => {
      sendMessage.mutate({ body: text });
    },
    [sendMessage],
  );

  const handleSelectTemplate = useCallback(
    (template: WhatsAppTemplate) => {
      sendMessage.mutate({
        templateName: template.name,
        templateLanguage: template.language,
      });
    },
    [sendMessage],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderMessage = useCallback(
    ({ item }: { item: WhatsAppMessage }) => <WhatsAppMessageBubble message={item} />,
    [],
  );

  const firstConversation = messagesData?.pages[0]?.items[0];
  const leadName = firstConversation
    ? (firstConversation as WhatsAppMessage & { conversation?: { lead?: { fullName?: string } } })
        ?.conversation?.lead?.fullName
    : undefined;

  const windowIsOpen = windowStatus?.isOpen ?? false;

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
    >
      <Box
        paddingX="md"
        paddingY="md"
        backgroundColor={colors.background.secondary}
        borderBottomWidth={1}
        borderBottomColor={colors.border.muted}
      >
        <HStack spacing="md" align="center">
          <Pressable onPress={() => router.back()} style={styles.backButton}>
            <ArrowLeft size={22} color={colors.text.primary} />
          </Pressable>
          <VStack spacing="xxs">
            <Text variant="body" color={colors.text.primary} style={styles.headerName}>
              {leadName || 'WhatsApp Chat'}
            </Text>
            <Text variant="caption" color={colors.text.tertiary}>
              {conversationId?.slice(0, 8)}...
            </Text>
          </VStack>
        </HStack>
      </Box>

      <WindowStatusBanner window={windowStatus} />

      {isLoading ? (
        <Box flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={colors.primary.main} />
        </Box>
      ) : (
        <FadeIn duration={400} style={styles.feed}>
          <FlatList
            ref={listRef}
            data={messages}
            keyExtractor={(item) => item.id}
            renderItem={renderMessage}
            contentContainerStyle={styles.messagesList}
            // Older messages live at the TOP now that the feed reads
            // chronologically, so the load trigger moves with them.
            onStartReached={handleLoadMore}
            onStartReachedThreshold={0.3}
            // Prepending a page would otherwise yank the viewport to the new
            // first row, losing the message the reader was looking at.
            maintainVisibleContentPosition={{ minIndexForVisible: 1 }}
            onContentSizeChange={scrollToLatest}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={
              isFetchingNextPage ? (
                <ActivityIndicator color={colors.primary.main} style={styles.loadMore} />
              ) : null
            }
          />
        </FadeIn>
      )}

      <MessageInputBar
        windowIsOpen={windowIsOpen}
        isSending={sendMessage.isPending}
        onSend={handleSend}
        onOpenTemplates={() => setShowTemplates(true)}
      />

      <TemplatePickerSheet
        visible={showTemplates}
        onClose={() => setShowTemplates(false)}
        templates={templates}
        isLoading={templatesLoading}
        isSyncing={syncTemplates.isPending}
        onSync={() => orgId && syncTemplates.mutate(orgId)}
        onSelect={handleSelectTemplate}
      />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.tertiary,
  },
  headerName: {
    fontWeight: tokens.fontWeight.semibold,
  },
  feed: {
    flex: 1,
  },
  messagesList: {
    paddingVertical: tokens.spacing.sm,
  },
  loadMore: {
    paddingVertical: tokens.spacing.md,
  },
});

function SignedInWhatsAppChat() {
  const { isAuthenticated, isLoading } = useAuth();
  const pathname = usePathname();

  if (isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" style={styles.root}>
        <ActivityIndicator size="large" color={colors.primary.main} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Redirect href={loginHrefFor(pathname)} />;
  }

  return <WhatsAppChatScreen />;
}

export default function WhatsAppChatRoute() {
  if (!isWhatsAppEnabled()) {
    return <Redirect href="/dashboard" />;
  }

  return <SignedInWhatsAppChat />;
}
